import {
  PROXY,
  applicantSection,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  legalInterestAttachment,
  legalInterestSection,
  resourceFeeInfo,
} from "modules/ebiForms/definitions/sharedGeodesyEnvironment";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * G1. Wniosek o wydanie wypisu lub wyrysu z operatu ewidencyjnego (ewidencja gruntów i budynków) — art. 24 ust. 3–5,
 * art. 40a–40b i 40g ustawy — Prawo geodezyjne i kartograficzne; wzór ogólnopolski: „Formularz EGiB” — załącznik nr 4 do
 * rozporządzenia MFiG z 28.07.2026 r. (Dz.U. 2026 poz. 1068), https://eli.gov.pl/api/acts/DU/2026/1068/text/O/D20261068.pdf
 * (pobrany 22.10.2026, s. 20–21). Pola i brzmienie jak we wzorze: 1. wnioskodawca (+ PESEL, dane kontaktowe), 6. oznaczenie
 * wniosku, 8. przedmiot wniosku, 9. dane nieruchomości, 10. uzasadnienie dostępu do danych podmiotów (przypisy 3 i 14),
 * 12. postać i sposób udostępnienia, 13. uwagi; pominięto pola wypełniane przez adresata i nieobowiązkową poz. 11.
 * Dwuetapowość (wniosek → Dokument Obliczenia Opłaty → wpłata → wydanie) obsługuje urząd po złożeniu. Odpis KRS
 * i dokument zwolnienia z opłaty — praktyka urzędów (do potwierdzenia z wydziałem).
 */
const PERSONAL_DATA: EbiCondition = {
  any: ["wypisRG", "wypisRGWyrys", "uproszczonyRG", "kartotekaBudynkow", "kartotekaLokali", "rejestrBudynkow", "rejestrLokali", "wykazPodmiotow"].map(
    (value): EbiCondition => ({ field: "przedmiot", includes: value }),
  ),
};
const PAPER: EbiCondition = { field: "postacDokumentow", equals: "papierowa" };
const ELECTRONIC: EbiCondition = { field: "postacDokumentow", equals: "elektroniczna" };
const OTHER_PICKUP: EbiCondition = {
  any: [
    { all: [PAPER, { field: "odbiorPapier", equals: "inny" }] },
    { all: [ELECTRONIC, { field: "odbiorElektroniczny", equals: "inny" }] },
  ],
};
const FEE_EXEMPTION: EbiCondition = { field: "zwolnienieZOplaty", equals: true };

/** Co najmniej jedno z pól identyfikujących — pozostałe przestają być wymagane, gdy któreś jest wypełnione. */
const IDENTIFIERS = ["jednostkaRejestrowa", "numeryDzialek", "numeryBudynkow", "numeryLokali", "ksiegaWieczysta"];
const noOtherIdentifier = (code: string): EbiCondition => ({
  not: { any: IDENTIFIERS.filter((c) => c !== code).map((c): EbiCondition => ({ field: c, filled: true })) },
});

const G1: IEbiFormDefinition = {
  code: "G1",
  version: 1,
  title: "Wniosek o wydanie wypisu lub wyrysu z ewidencji gruntów i budynków",
  category: EEbiFormCategory.Geodesy,
  office: EEbiFormOffice.County,
  description: "Wypis z rejestru gruntów, budynków lub lokali, wyrys z mapy ewidencyjnej, wypis z kartoteki — formularz EGiB.",
  keywords: ["wypis", "wyrys", "ewidencja gruntów", "egib", "rejestr gruntów", "mapa ewidencyjna", "kartoteka budynków", "geodezja"],
  organ: "Starosta (prezydent miasta na prawach powiatu) — ośrodek dokumentacji geodezyjnej i kartograficznej",
  legalBasis:
    "art. 24 ust. 3–5 i art. 40a–40b ustawy z dnia 17 maja 1989 r. — Prawo geodezyjne i kartograficzne; rozporządzenie Ministra Finansów i Gospodarki z dnia 28 lipca 2026 r. (Dz.U. 2026 poz. 1068) — wzór: załącznik nr 4 „Formularz EGiB”",
  request:
    "Wnoszę o wydanie wypisu lub wyrysu z operatu ewidencyjnego w zakresie wskazanym w części 8, dla nieruchomości opisanej w części 9.",
  feeLabel: "wg stawek",
  fee: "Opłata za udostępnienie według tabeli nr 11 załącznika do Prawa geodezyjnego i kartograficznego (stawki bazowe w postaci papierowej: wypis z rejestru gruntów 50 zł, wyrys z mapy ewidencyjnej 110 zł, wypis z wyrysem 150 zł; waloryzowane co roku) — ustalana w Dokumencie Obliczenia Opłaty i wnoszona przed wydaniem. Bez opłaty skarbowej; pełnomocnictwo — 17 zł.",
  sections: [
    applicantSection("1. Wnioskodawca"),
    representationSection(),
    {
      code: "przedmiotWniosku",
      title: "8. Przedmiot wniosku",
      fields: [
        {
          code: "przedmiot",
          type: "checkboxes",
          label: "Wnoszę o wydanie",
          required: true,
          hint: "Pozycje oznaczone (*) zawierają lub mogą zawierać dane osobowe — aby je otrzymać, wypełnij część 10.",
          options: [
            { value: "wypisRG", label: "Wypis z rejestru gruntów (*)" },
            { value: "wypisRGWyrys", label: "Wypis z rejestru gruntów oraz wyrys z mapy ewidencyjnej (*)" },
            { value: "wyrys", label: "Wyrys z mapy ewidencyjnej" },
            { value: "uproszczonyRG", label: "Uproszczony wypis z rejestru gruntów (*)" },
            { value: "rgBezDanych", label: "Wypis z rejestru gruntów bez danych osobowych" },
            { value: "kartotekaBudynkow", label: "Wypis z kartoteki budynków (*)" },
            { value: "kartotekaLokali", label: "Wypis z kartoteki lokali (*)" },
            { value: "rejestrBudynkow", label: "Wypis z rejestru budynków (*)" },
            { value: "rejestrLokali", label: "Wypis z rejestru lokali (*)" },
            { value: "wykazDzialek", label: "Wypis z wykazu działek ewidencyjnych" },
            { value: "wykazPodmiotow", label: "Wypis z wykazu podmiotów (*)" },
          ],
        },
        {
          code: "oznaczenieWnioskodawcy",
          type: "text",
          label: "Oznaczenie wniosku nadane przez wnioskodawcę (nieobowiązkowo)",
          width: "half",
          maxLength: 100,
        },
      ],
    },
    {
      code: "nieruchomosc",
      title: "9. Dane identyfikujące nieruchomość, której dotyczy wniosek",
      description:
        "Podaj obręb ewidencyjny albo adres nieruchomości oraz co najmniej jeden numer: jednostki rejestrowej, działek, budynków, lokali albo księgi wieczystej.",
      fields: [
        { code: "powiat", type: "text", label: "Powiat", required: true, width: "half", maxLength: 100 },
        { code: "gmina", type: "text", label: "Gmina", required: true, width: "half", maxLength: 100 },
        {
          code: "obreb",
          type: "text",
          label: "Obręb ewidencyjny",
          requiredWhen: { not: { field: "adresNieruchomosci", filled: true } },
          width: "half",
          maxLength: 100,
        },
        {
          code: "adresNieruchomosci",
          type: "text",
          label: "lub adres nieruchomości",
          requiredWhen: { not: { field: "obreb", filled: true } },
          width: "half",
          maxLength: 200,
        },
        {
          code: "jednostkaRejestrowa",
          type: "text",
          label: "Nr jednostki rejestrowej",
          requiredWhen: noOtherIdentifier("jednostkaRejestrowa"),
          width: "half",
          maxLength: 100,
        },
        {
          code: "numeryDzialek",
          type: "text",
          label: "lub nr działek ewidencyjnych",
          requiredWhen: noOtherIdentifier("numeryDzialek"),
          width: "half",
          maxLength: 500,
          placeholder: "np. 125/4, 126",
        },
        { code: "numeryBudynkow", type: "text", label: "lub nr budynków", requiredWhen: noOtherIdentifier("numeryBudynkow"), width: "half", maxLength: 300 },
        { code: "numeryLokali", type: "text", label: "lub nr lokali", requiredWhen: noOtherIdentifier("numeryLokali"), width: "half", maxLength: 300 },
        {
          code: "ksiegaWieczysta",
          type: "text",
          label: "lub nr księgi wieczystej",
          requiredWhen: noOtherIdentifier("ksiegaWieczysta"),
          width: "half",
          maxLength: 30,
          placeholder: "np. LU1I/00012345/6",
        },
      ],
    },
    legalInterestSection(
      PERSONAL_DATA,
      "10. Uzasadnienie wniosku o wydanie wypisu zawierającego dane podmiotów (art. 20 ust. 2 pkt 1 PGiK) lub numer księgi wieczystej",
      "Wypełnienie tej części jest obowiązkowe przy wypisach oznaczonych (*) — art. 24 ust. 5 ustawy — Prawo geodezyjne i kartograficzne.",
    ),
    {
      code: "postac",
      title: "12. Postać dokumentów i sposób ich udostępnienia",
      fields: [
        {
          code: "postacDokumentow",
          type: "radio",
          label: "Postać dokumentów",
          required: true,
          options: [
            { value: "papierowa", label: "postać papierowa" },
            { value: "elektroniczna", label: "postać elektroniczna" },
          ],
        },
        {
          code: "odbiorPapier",
          type: "radio",
          label: "Sposób odbioru",
          required: true,
          visibleWhen: PAPER,
          options: [
            { value: "osobiscie", label: "odbiór osobisty w siedzibie organu" },
            { value: "wysylkaNaglowek", label: "wysyłka pod adres jak w nagłówku (adres do korespondencji wnioskodawcy)" },
            { value: "wysylkaInny", label: "wysyłka pod inny adres" },
            { value: "inny", label: "inny sposób odbioru" },
          ],
        },
        {
          code: "odbiorElektroniczny",
          type: "radio",
          label: "Sposób odbioru",
          required: true,
          visibleWhen: ELECTRONIC,
          defaultValue: "ebi",
          options: [
            { value: "ebi", label: "w Elektronicznym Biurze Interesanta" },
            { value: "inny", label: "inny sposób odbioru" },
          ],
        },
        {
          code: "adresWysylki",
          type: "text",
          label: "Adres do wysyłki",
          required: true,
          visibleWhen: { all: [PAPER, { field: "odbiorPapier", equals: "wysylkaInny" }] },
          maxLength: 300,
        },
        { code: "innySposobOdbioru", type: "text", label: "Inny sposób odbioru", required: true, visibleWhen: OTHER_PICKUP, maxLength: 300 },
      ],
    },
    {
      code: "uwagi",
      title: "13. Dodatkowe wyjaśnienia i opłata",
      fields: [
        { code: "uwagiWnioskodawcy", type: "textarea", label: "Dodatkowe wyjaśnienia i uwagi wnioskodawcy (nieobowiązkowo)", maxLength: 3000 },
        resourceFeeInfo("infoOplata", "tabeli nr 11"),
        { code: "zwolnienieZOplaty", type: "checkbox", label: "Ubiegam się o zwolnienie z opłaty (art. 40a ust. 2 ustawy — Prawo geodezyjne i kartograficzne)" },
        { code: "podstawaZwolnienia", type: "text", label: "Podstawa zwolnienia z opłaty", required: true, visibleWhen: FEE_EXEMPTION, maxLength: 300 },
      ],
    },
    replySection("Sposób odbioru korespondencji w sprawie (w tym Dokumentu Obliczenia Opłaty)"),
  ],
  attachments: [
    legalInterestAttachment(PERSONAL_DATA, true),
    {
      code: "dokumentZwolnienia",
      label: "Dokument potwierdzający uprawnienie do zwolnienia z opłaty",
      hint: "Praktyka urzędów — urząd może go zażądać (art. 40a ust. 2).",
      visibleWhen: FEE_EXEMPTION,
    },
    ...proxyAttachments(),
    {
      code: "odpisKrs",
      label: "Odpis z KRS mocodawcy będącego osobą prawną",
      hint: "Praktyka urzędów — zamiast odpisu możesz podać numer KRS w uwagach.",
      visibleWhen: PROXY,
    },
    otherAttachments(),
  ],
};

export default G1;
